export class FetchTimeoutError extends Error {
  constructor(timeoutMs) {
    super(`Request timed out after ${timeoutMs}ms`);
    this.name = "TimeoutError";
    this.code = "REQUEST_TIMEOUT";
    this.timeoutMs = timeoutMs;
  }
}

export class InvalidJsonResponseError extends Error {
  constructor(status) {
    super("The server returned an invalid response.");
    this.name = "InvalidJsonResponseError";
    this.code = "INVALID_JSON_RESPONSE";
    this.status = status;
  }
}

async function withAbortDeadline(operation, { timeoutMs, signal } = {}) {
  const safeTimeoutMs = Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : 10000;
  const controller = new AbortController();
  let timedOut = false;
  let rejectBoundary = () => {};
  const boundary = new Promise((_, reject) => {
    rejectBoundary = reject;
  });
  const handleExternalAbort = () => {
    const reason = signal?.reason || new DOMException("Aborted", "AbortError");
    controller.abort(reason);
    rejectBoundary(reason);
  };
  const timeoutId = setTimeout(() => {
    timedOut = true;
    const error = new FetchTimeoutError(safeTimeoutMs);
    controller.abort(error);
    rejectBoundary(error);
  }, safeTimeoutMs);

  try {
    if (signal) {
      if (signal.aborted) {
        handleExternalAbort();
      } else {
        signal.addEventListener("abort", handleExternalAbort, { once: true });
      }
    }
    return await Promise.race([operation(controller.signal), boundary]);
  } catch (error) {
    if (timedOut) {
      throw new FetchTimeoutError(safeTimeoutMs);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener?.("abort", handleExternalAbort);
  }
}

export async function fetchWithTimeout(url, options = {}) {
  const { timeoutMs, signal, ...fetchOptions } = options || {};
  return withAbortDeadline(
    (requestSignal) => fetch(url, { ...fetchOptions, signal: requestSignal }),
    { timeoutMs, signal }
  );
}

export async function fetchJsonWithTimeout(url, options = {}) {
  const { timeoutMs, signal, ...fetchOptions } = options || {};
  return withAbortDeadline(
    async (requestSignal) => {
      const response = await fetch(url, { ...fetchOptions, signal: requestSignal });
      let payload = {};
      if (response.status !== 204) {
        try {
          payload = await response.json();
        } catch (error) {
          if (requestSignal.aborted) throw requestSignal.reason || error;
          if (response.ok) throw new InvalidJsonResponseError(response.status);
        }
      }
      return { response, payload };
    },
    { timeoutMs, signal }
  );
}
